// @ts-check
// Chamadas de metadados do editor de modelo de dados e das etapas do funil
// (docs/arquitetura-appcrm/08-contrato-modelo-de-dados.md). Depois de cada mudança o bootstrap é relido,
// para tabela, kanban, filtros e painéis enxergarem os metadados novos.
import { api } from './api-client.js';
import { loadBootstrap } from './metadata-store.js';

/**
 * @typedef {import('../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {import('../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 */

/** @param {any} body @param {string} key */
const readData = (body, key) => body?.data?.[key] ?? body?.data ?? body;

/**
 * @param {{ nameSingular: string, namePlural: string, labelSingular: string, labelPlural: string, description?: string, icon?: string }} input
 * @returns {Promise<ObjectMetadata>}
 */
export async function createObjectMetadata(input) {
  const object = readData(await api.post('/rest/metadata/objects', input), 'createObject');
  await loadBootstrap();
  return object;
}

/**
 * @param {string} objectMetadataId @param {Record<string, unknown>} patch
 * @returns {Promise<ObjectMetadata>}
 */
export async function updateObjectMetadata(objectMetadataId, patch) {
  const object = readData(await api.patch(`/rest/metadata/objects/${objectMetadataId}`, patch), 'updateObject');
  await loadBootstrap();
  return object;
}

/** @param {string} objectMetadataId */
export async function deleteObjectMetadata(objectMetadataId) {
  await api.delete(`/rest/metadata/objects/${objectMetadataId}`);
  await loadBootstrap();
}

/**
 * @param {Record<string, unknown>} input
 * @returns {Promise<FieldMetadata>}
 */
export async function createFieldMetadata(input) {
  const field = readData(await api.post('/rest/metadata/fields', input), 'createField');
  await loadBootstrap();
  return field;
}

/**
 * `optionMigrations` leva os registros de cada opção removida para a opção escolhida (null deixa sem valor).
 * @param {string} fieldMetadataId @param {Record<string, unknown>} patch
 * @returns {Promise<FieldMetadata>}
 */
export async function updateFieldMetadata(fieldMetadataId, patch) {
  const field = readData(await api.patch(`/rest/metadata/fields/${fieldMetadataId}`, patch), 'updateField');
  await loadBootstrap();
  return field;
}

/** @param {string} fieldMetadataId */
export async function deleteFieldMetadata(fieldMetadataId) {
  await api.delete(`/rest/metadata/fields/${fieldMetadataId}`);
  await loadBootstrap();
}

/**
 * Quantos registros usam cada opção de um campo de seleção.
 * @param {string} fieldMetadataId
 * @returns {Promise<Record<string, number>>}
 */
export async function fetchFieldOptionUsage(fieldMetadataId) {
  const body = await api.get(`/rest/metadata/fields/${fieldMetadataId}/option-usage`);
  return body?.data?.optionUsage ?? {};
}

/**
 * @typedef {{ roleId: string, roleLabel: string, visibleStageValues: string[], handoffStageValues: string[] }} FunnelStageScopeSummary
 */

/**
 * Contagens por opção e, no campo de etapa do card, os papéis restritos por etapa (contrato de equipe, seção 3.2): o
 * editor do funil avisa quem deixa de ver uma etapa nova ou o destino de uma excluída.
 * @param {string} fieldMetadataId
 * @returns {Promise<{ optionUsage: Record<string, number>, stageScopes: FunnelStageScopeSummary[] }>}
 */
export async function fetchFieldOptionUsageWithScopes(fieldMetadataId) {
  const body = await api.get(`/rest/metadata/fields/${fieldMetadataId}/option-usage`);
  return { optionUsage: body?.data?.optionUsage ?? {}, stageScopes: Array.isArray(body?.data?.stageScopes) ? body.data.stageScopes : [] };
}
